/**
 * Financials-style datatable query for Listings & Bookings SSRM.
 * Backend expects page / pageSize / sortModel / filterModel (JSON strings when set).
 */
export interface ShipmentGridQuery {
  page: number;
  pageSize: number;
  sortModel: string | unknown[];
  filterModel: string | unknown[];
}

type ShipmentGridPayload = {
  rows?: unknown[];
  rowData?: unknown[];
  data?: unknown[] | ShipmentGridPayload;
  total?: number | string;
  lastRow?: number | string;
  rowCount?: number | string;
};

type ShipmentGridResponse = ShipmentGridPayload | null | undefined;

/** Prefer Financials-style top-level payload; also accept `{ data: { rows, total } }`. */
function payload(res: ShipmentGridResponse): ShipmentGridPayload | null | undefined {
  const nested = res?.data;
  if (nested && !Array.isArray(nested) && (nested.rows || nested.rowData || nested.total != null)) {
    return nested;
  }
  return res;
}

export function shipmentGridRows(res: ShipmentGridResponse): unknown[] {
  const body = payload(res);
  if (Array.isArray(body?.rows)) {
    return body.rows;
  }
  if (Array.isArray(body?.rowData)) {
    return body.rowData;
  }
  if (Array.isArray(body?.data)) {
    return body.data;
  }
  const nested = (body?.data as ShipmentGridPayload | undefined)?.data;
  return Array.isArray(nested) ? nested : [];
}

export function shipmentGridTotal(res: ShipmentGridResponse): number {
  const body = payload(res);
  return Number(body?.total ?? body?.lastRow ?? body?.rowCount ?? 0) || 0;
}

export function buildShipmentGridQuery(options: {
  startRow: number;
  endRow: number;
  sortModel?: { colId?: string; sort?: string }[] | null;
  filterModel?: Record<string, any> | null;
  maxPageSize?: number;
}): ShipmentGridQuery {
  const maxPageSize = options.maxPageSize ?? 200;
  const windowSize = Math.max(options.endRow - options.startRow, 1);
  const pageSize = Math.min(maxPageSize, windowSize);

  return {
    page: Math.floor(options.startRow / pageSize) + 1,
    pageSize,
    sortModel: serialize((options.sortModel ?? []).filter((sort) => !!sort.colId)),
    filterModel: serialize(options.filterModel),
  };
}

function serialize(model: unknown[] | Record<string, any> | null | undefined): string | unknown[] {
  const empty = !model || (Array.isArray(model) ? model.length === 0 : Object.keys(model).length === 0);
  return empty ? [] : JSON.stringify(model);
}
